import { useEffect, useState } from 'react';
import KoobModal from './KoobModal';
import KoobButton from './KoobButton';
import KoobTitle from './KoobTitle';
import KoobParagraph from './KoobParagraph';

interface OrganizationChoice {
  id: string;
  name: string;
}

interface KoobOrganizationRequiredPromptProps {
  /** The user's organizations, to map the API's encoded ids to raw ids. */
  organizations: { id: string; organizationId: string | number }[];
  /** Name of the window event the API client dispatches (KD-3735). */
  eventName: string;
  onChoose: (organizationId: string) => void;
  title: string;
  text: string;
}

/**
 * Asks which organization an action is for, when the API answered 400
 * ORGANIZATION_REQUIRED (a group member working on "All"). The choice switches
 * the app's one organization picker; the user then repeats the action.
 */
export default function KoobOrganizationRequiredPrompt({
  organizations,
  eventName,
  onChoose,
  title,
  text,
}: KoobOrganizationRequiredPromptProps) {
  const [choices, setChoices] = useState<OrganizationChoice[]>([]);

  useEffect(() => {
    const listener = (event: Event) =>
      setChoices((event as CustomEvent<OrganizationChoice[]>).detail ?? []);
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }, [eventName]);

  const choose = (encodedId: string) => {
    const organization = organizations.find((org) => org.id === encodedId);
    onChoose(String(organization?.organizationId ?? encodedId));
  };

  return (
    <KoobModal
      open={choices.length > 0}
      setOpen={(open) => !open && setChoices([])}
      widthClass="sm:max-w-md"
    >
      <KoobTitle size="text-lg">{title}</KoobTitle>
      <KoobParagraph size="sm">{text}</KoobParagraph>
      <div className="mt-4 flex flex-col gap-2">
        {choices.map((choice) => (
          <KoobButton
            key={choice.id}
            onClick={() => choose(choice.id)}
            color="secondary"
          >
            {choice.name}
          </KoobButton>
        ))}
      </div>
    </KoobModal>
  );
}
